import React, { useState, useEffect } from "react";
import { Save2, CloseCircle } from "iconsax-react";
import { motion } from "framer-motion";
import { enqueueSnackbar } from "notistack";
import MainButton from "../button/MainButton";
import TextField from "../fields/TextField";
import UnitToggle from "../shared/UnitToggle";
import { useAuth } from "../../contexts/AuthContext";
import { useGlobalInfo } from "../../contexts/GlobalInfoContext";
import { updateBrandChart } from "../../utilities/api/sizeChartService";
import { CM_TO_INCH, INCH_TO_CM } from "../../contexts/BodyMeasurementServiceContext";

interface EditSizeChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  chartData: any;
  chartPath: string[];
  fullChartData: any;
  focusSize?: string;
}

const EditSizeChartModal: React.FC<EditSizeChartModalProps> = ({ isOpen, onClose, onSave, chartData, chartPath, fullChartData, focusSize }) => {
  const { accessToken } = useAuth();
  const { userInfo } = useGlobalInfo();
  const [editedData, setEditedData] = useState<any>({});
  const [selectedUnit, setSelectedUnit] = useState<"cm" | "inch">("cm");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (chartData) {
      setEditedData(JSON.parse(JSON.stringify(chartData)));
    }
  }, [chartData]);

  const convertValueForDisplay = (value: number): string => {
    if (selectedUnit === "inch") {
      return (value * CM_TO_INCH).toFixed(1);
    }
    return value.toString();
  };

  const convertInputValueToCm = (inputValue: string): number => {
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) return 0;
    return selectedUnit === "inch" ? numValue * INCH_TO_CM : numValue;
  };

  const handleValueChange = (sizeName: string, variantIndex: number, measurementKey: string, value: string) => {
    const numericValue = convertInputValueToCm(value);

    setEditedData((prev: any) => {
      const newData = { ...prev };
      if (newData[sizeName] && newData[sizeName][variantIndex]) {
        newData[sizeName][variantIndex][measurementKey] = numericValue;
      }
      return newData;
    });
  };

  const handleSave = async () => {
    if (!accessToken || !userInfo?.id) return;

    try {
      setIsSaving(true);

      const updatedFullData = JSON.parse(JSON.stringify(fullChartData));

      let current = updatedFullData;
      for (let i = 0; i < chartPath.length - 1; i++) {
        current = current[chartPath[i]];
      }

      const lastPathKey = chartPath[chartPath.length - 1];
      if (current[lastPathKey] && Array.isArray(current[lastPathKey])) {
        const updatedSizes: any[] = [];
        Object.entries(editedData).forEach(([sizeName, variants]: [string, any]) => {
          variants.forEach((variant: any) => {
            updatedSizes.push(variant);
          });
        });
        current[lastPathKey] = updatedSizes;
      }

      const response = await updateBrandChart(accessToken, {
        Brand: String(userInfo.id),
        ChartData: updatedFullData,
      });

      if (response.ok) {
        enqueueSnackbar("Size chart updated successfully!", { variant: "success" });
        onSave();
        onClose();
      } else {
        enqueueSnackbar("Failed to update size chart", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error updating size chart", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl"
      >
        <div className="flex items-center justify-between p-6 border-b border-gorgonzola-blue-50">
          <div className="flex items-center gap-4">
            <h3 className="text-base font-inter-bold text-closed-shutter-300">Edit Size Chart</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-closed-shutter-300">Unit:</span>
              <UnitToggle selectedUnit={selectedUnit} onUnitChange={setSelectedUnit} />
            </div>
          </div>
          <button onClick={onClose} className="p-2 transition-colors rounded-lg hover:bg-gray-100">
            <CloseCircle className="w-5 h-5 text-closed-shutter-300" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="text-sm text-closed-shutter-200">Editing: {chartPath.join(" > ")}</div>

          {Object.entries(editedData).map(([sizeName, variants]: [string, any]) => (
            <div key={sizeName} className="p-4 border rounded-lg">
              <h4 className="mb-4 font-inter-bold text-closed-shutter-300">Size: {sizeName}</h4>

              <div className="space-y-4">
                {variants.map((variant: any, variantIndex: number) => (
                  <div key={variantIndex} className="p-4 rounded-lg bg-gray-50">
                    {/* <h5 className="mb-3 font-inter-medium text-closed-shutter-300">Item {variantIndex + 1}</h5> */}

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {Object.entries(variant).map(([key, value]) => {
                        // if (key === "Size" || key === "EuropeSize") {
                        //   return (
                        //     <div key={key} className="space-y-1">
                        //       <label className="block text-xs text-closed-shutter-300">{key}</label>
                        //       <div className="px-3 py-2 text-xs bg-gray-100 border rounded-lg text-closed-shutter-300">{String(value)}</div>
                        //     </div>
                        //   );
                        // }

                        if (typeof value === "number") {
                          return (
                            <TextField
                              key={key}
                              label={`${key} (${selectedUnit})`}
                              placeholder="Enter value"
                              value={convertValueForDisplay(value)}
                              onChange={(e) => handleValueChange(sizeName, variantIndex, key, (e.target as HTMLInputElement).value)}
                            />
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gorgonzola-blue-50">
          <MainButton onClick={onClose} type="secondary">
            Cancel
          </MainButton>
          <MainButton onClick={handleSave} type="primary" disabled={isSaving}>
            {isSaving ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Save2 className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </MainButton>
        </div>
      </motion.div>
    </div>
  );
};

export default EditSizeChartModal;
